import { useEffect, useState } from 'react';
import { FieldValues, DeepMap, FieldError, UseFormSetValue, Controller, Control } from 'react-hook-form';
import { TSelectOption, TypeaheadOptions } from '../../../util/types';
import EditButton from '../EditButton';
import generateErrorMarkup from '../generateErrorMarkup';

import { Typeahead as RBTypeahead } from 'react-bootstrap-typeahead';
import 'react-bootstrap-typeahead/css/Typeahead.scss';

type Props = {
  name: string;
  label: string;
  options: TSelectOption[];
  optionsUrl?: string;
  // Optional default value (will revert to formData[name] if none)
  defaultValue?: TypeaheadOptions;
  disclaimer?: React.ReactElement;
  showEditBtn: boolean;
  editable: boolean;
  validationObj?: any; // TODO: add types
  hidden?: boolean;

  control: Control<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  errors: DeepMap<FieldValues, FieldError>;
  formData: any;
  returnToStep?: (name: string) => void;
}

/**
 * A Select component
 * @param props
 */
let Typeahead = (props: Props) => {

  // https://www.carlrippon.com/drop-down-data-binding-with-react-hooks/
  // TODO: 'loading' never shows because i set the first one to be props.label
  const [items, setItems] = useState(props.optionsUrl ? [{ label: 'loading...', value: '' }] : props.options);
  const [loading, setLoading] = useState(props.optionsUrl ? true : false);

  const generatedDefaultValue = props.defaultValue ?? props.formData[props.name] ?? '';

  const {
    optionsUrl,
    defaultValue,
    setValue,
    name,
    formData,
  } = props;
  useEffect(() => {
    let unmounted = false;

    async function getOptions() {
      // console.debug('Getting options for', name, `unmounted: ${unmounted}`);
      // TODO: auth this at all?
      const isDev = process.env.NODE_ENV !== 'production';
      const url = `${isDev ? 'http://localhost:8000' : ''}${optionsUrl}`;
      const response = await fetch(url);
      const parsedOptions = await response.json();
      if (!unmounted) {
        setItems(parsedOptions);
        setLoading(false);

        // console.log('Setting the Select value to ', props.defaultValue);
        setValue(name, generatedDefaultValue);
      }
    };

    if (optionsUrl) {
      getOptions();
    }

    return () => {
      unmounted = true;
    };
  }, [optionsUrl,
      defaultValue,
      setValue,
      name,
      formData,
      generatedDefaultValue,
  ]);

  // TODO: is this fine or do i need to save state somehow?
  if (props.hidden) return null;

  // TODO: react-hook-form probably has a better way of passing refs around
  // but this seems to work for now...
  const { errors } = props;

  const clickEditBtn = (e: React.MouseEvent) => {
    // Only has one if added for reviewMode
    if (props.returnToStep) props.returnToStep(props.name);
  };

  // Add default value with label
  const options = [
    <option
      value=''
      key={0}
    >
      {props.validationObj?.required ? `${props.label}*` : props.label}
    </option>
  ];
  options.push(...items.map((option, i) => {
    const optionEl =
      <option
        value={option.value}
        key={i + 1}
      >
        {option.label}
      </option>
    return optionEl;
  }));

  const validationObj = {
    ...props.validationObj,
  };

  const required = validationObj.required;

  if (required) {
    // Validate empty inputs, since they still get through the RHF "required" test
    if (!validationObj.validate) validationObj.validate = {};
    validationObj.validate.notEmpty = (selectedOptions: TypeaheadOptions) => {
      // If value is empty, returns the set validation message (validationObj.required IS the message)
      const ret = selectedOptions[0].value !== '' || validationObj.required;
      return ret;
    }
  }

  const elem =
  <div className="Typeahead">
    <label>
      <span className="sr-only">
        {`${props.label}:`}
      </span>

      <Controller
        // as={rbTypeahead}
        control={props.control}
        name={props.name}
        rules={validationObj}
        // clearButton
        // multiple
        // options={[]}

        // onChange={(selected) => {
        //   // Handle selections...
        // }}

        // TODO: RHF sample has the inner props set on the Controller (and expanded) but TS doens't like that
        render={({ field }) => {
          // console.debug({ field, fieldState, value: field.value?.[0]?.label });
          return <RBTypeahead {...field}
            // These are sent automatically because of the {...field} above
            // onBlur={field.onBlur} // notify when input is touched
            // onChange={field.onChange} // send value to hook form
            onBlur={() => {
              field.onBlur();
            }} // notify when input is touched
            onChange={(e) => {
              field.onChange(e);
            }} // send value to hook form

            id={props.name}
            options={items}
            // NOTE: getValues doesn't return the value if the input is disabled!
            disabled={!props.editable || loading}
            defaultSelected={generatedDefaultValue}
            placeholder={required ? `${props.label}*` : props.label}
            key={props.name}
            inputProps={
              {
                className: errors[props.name] ? 'parsley-error' : undefined,
                'aria-describedby': errors[props.name] ? `${props.name}-errors` : undefined,
                name: props.name,
                id: props.name,
              }
            }
        />}}
      />
      { props.showEditBtn ? <EditButton onClick={clickEditBtn} /> : null }
    </label>
    { generateErrorMarkup(errors, props.name) }
    {props.disclaimer ?
      <div className="Field__Disclaimer">
        {props.disclaimer}
      </div>
    : null}
  </div>;

  return (
    <>
      {elem}
    </>
  );
};

export default Typeahead;
